/**
 * Toasts callable from anywhere — a Pinia store, a router guard, a fetch
 * handler — none of which sit inside the component tree.
 *
 * naive-ui's discrete API renders outside <NConfigProvider>, so it needs its
 * own theme. Rather than restating the palette (the third copy of every hex in
 * the project this came from), it reads the same tokens off the document and
 * builds the API lazily, on first use, when the stylesheets are certainly in.
 */

import { createDiscreteApi, darkTheme, type MessageApi } from 'naive-ui';
import { naiveThemeOverrides } from '@/styles/tokens';

let api: MessageApi | null = null;

function messageApi(): MessageApi {
  if (api) return api;
  const { message } = createDiscreteApi(['message'], {
    configProviderProps: {
      theme: darkTheme,
      themeOverrides: naiveThemeOverrides(),
    },
    messageProviderProps: {
      placement: 'bottom-right',
      duration: 5000,
    },
  });
  api = message;
  return api;
}

/**
 * Durations are long on purpose. These carry the API's own sentences — the
 * capacity 409s and port 422s that name the way out — and those take a moment
 * to read. Errors do not auto-dismiss at all.
 */
export const appMessage = {
  success: (content: string) => messageApi().success(content, { duration: 4000 }),
  info: (content: string) => messageApi().info(content, { duration: 5000 }),
  warning: (content: string) => messageApi().warning(content, { duration: 8000 }),
  error: (content: string) => messageApi().error(content, { duration: 0, closable: true }),
};
